/**
 * Project utilities - migrated to use backend API
 */

import { api } from "@/lib/api";
import type { Project, ProjectMember } from "@/types";

export interface ProjectWithMembers extends Project {
  members?: ProjectMember[];
  memberCount?: number;
  memberProfiles?: Record<string, { name: string; avatar?: string }>;
}

/**
 * Normalize project member from backend (snake_case) to frontend (camelCase)
 */
function normalizeMember(member: any): ProjectMember {
  return {
    userId: member.user_id || member.userId,
    role: member.role || 'member',
    joinedAt: member.joined_at ? new Date(member.joined_at) : (member.joinedAt || new Date()),
  };
}

/**
 * Normalize project from backend (snake_case) to frontend (camelCase)
 */
function normalizeProject(data: any): ProjectWithMembers {
  return {
    id: data.id,
    title: data.title,
    description: data.description,
    thumbnail: data.thumbnail,
    bannerUrl: data.banner_url || data.bannerUrl,
    logoUrl: data.logo_url || data.logoUrl,
    websiteUrl: data.website_url || data.websiteUrl,
    twitterUrl: data.twitter_url || data.twitterUrl,
    discordUrl: data.discord_url || data.discordUrl,
    country: data.country,
    superteam: data.superteam,
    status: data.status,
    difficulty: data.difficulty,
    tags: data.tags || [],
    teamId: data.team_id || data.teamId,
    createdBy: data.created_by || data.createdBy,
    createdAt: data.created_at ? new Date(data.created_at) : (data.createdAt || new Date()),
    updatedAt: data.updated_at ? new Date(data.updated_at) : (data.updatedAt || new Date()),
    repositoryUrl: data.repository_url || data.repositoryUrl,
    liveUrl: data.live_url || data.liveUrl,
    progressLog: data.progress_log || data.progressLog,
    members: Array.isArray(data.members) ? data.members.map(normalizeMember) : [],
    memberCount: data.member_count || data.memberCount || (data.members?.length || 0),
  };
}

/**
 * Get all projects
 */
export async function getAllProjects(params?: {
  status?: string;
  difficulty?: string;
  teamId?: string;
  limit?: number;
}): Promise<ProjectWithMembers[]> {
  try {
    const search = new URLSearchParams();
    if (params?.status) search.set('status', params.status);
    if (params?.difficulty) search.set('difficulty', params.difficulty);
    if (params?.teamId) search.set('teamId', params.teamId);
    if (params?.limit) search.set('limit', String(params.limit));
    const suffix = search.toString() ? `?${search.toString()}` : '';
    const data = await api<any[]>(`/projects${suffix}`);
    return (data || []).map(normalizeProject);
  } catch (error) {
    console.error('Error fetching projects:', error);
    return [];
  }
}

/**
 * Get project by ID
 */
export async function getProjectById(id: string): Promise<ProjectWithMembers | null> {
  try {
    const data = await api<any>(`/projects/${id}`);
    return data ? normalizeProject(data) : null;
  } catch (error) {
    console.error('Error fetching project:', error);
    return null;
  }
}

/**
 * Convert a project title to a stable URL slug.
 * Must match the backend logic in `/api/projects/slug/:slug`.
 */
export function slugifyProjectTitle(title: string): string {
  return String(title)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function formatProjectStatus(status: Project["status"]): string {
  if (status === "completed") return "live";
  return status;
}

/**
 * Get project by slug (computed from title, not stored in DB).
 */
export async function getProjectBySlug(slug: string): Promise<ProjectWithMembers | null> {
  try {
    const data = await api<any>(`/projects/slug/${encodeURIComponent(slug)}`);
    return data ? normalizeProject(data) : null;
  } catch (error) {
    console.error('Error fetching project by slug:', error);
    return null;
  }
}

function looksLikeUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value
  );
}

/**
 * Resolve a "project key" which can be either:
 * - UUID project id (legacy URLs)
 * - slugified title (new shareable URLs)
 */
export async function getProjectByIdOrSlug(projectKey: string): Promise<ProjectWithMembers | null> {
  if (!projectKey) return null;

  // Optimistic path for legacy URLs
  if (looksLikeUuid(projectKey)) {
    const byId = await getProjectById(projectKey);
    if (byId) return byId;
  }

  // Slug path
  const bySlug = await getProjectBySlug(projectKey);
  if (bySlug) return bySlug;

  // Fallback to id fetch for any non-UUID formats
  return getProjectById(projectKey);
}

/**
 * Create a new project
 */
export async function createProject(
  projectData: {
    title: string;
    description: string;
    status: Project['status'];
    difficulty: Project['difficulty'];
    tags: string[];
    thumbnail?: string;
    bannerUrl?: string;
    logoUrl?: string;
    websiteUrl?: string;
    twitterUrl?: string;
    discordUrl?: string;
    country?: string;
    superteam?: string;
    teamId?: string;
    repositoryUrl?: string;
    liveUrl?: string;
    progressLog?: string;
  },
  userId: string
): Promise<Project | null> {
  try {
    const data = await api<any>('/projects', {
      method: 'POST',
      body: JSON.stringify({
        ...projectData,
        created_by: userId,
      }),
    });
    // Normalize the response from backend (snake_case) to frontend (camelCase)
    const normalized = normalizeProject(data);
    return normalized;
  } catch (error: any) {
    console.error('Error creating project:', error?.message || error);
    return null;
  }
}

/**
 * Update a project
 */
export async function updateProject(
  id: string,
  updates: Partial<{
    title: string;
    description: string;
    status: Project['status'];
    difficulty: Project['difficulty'];
    tags: string[];
    teamId: string;
    repositoryUrl: string;
    liveUrl: string;
    progressLog: string;
    bannerUrl: string;
    logoUrl: string;
    websiteUrl: string;
    twitterUrl: string;
    discordUrl: string;
    country: string;
    superteam: string;
  }>
): Promise<boolean> {
  try {
    await api(`/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return true;
  } catch (error) {
    console.error('Error updating project:', error);
    return false;
  }
}

/**
 * Delete a project
 */
export async function deleteProject(id: string): Promise<boolean> {
  try {
    await api(`/projects/${id}`, {
      method: 'DELETE',
    });
    return true;
  } catch (error) {
    console.error('Error deleting project:', error);
    return false;
  }
}
